import { ExternalLink, KeyRound, Plus, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

import { CopyButton } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { useCompanyAccesses, vaultDeepLink } from './api'
import { AccessForm } from './components/AccessForm'
import { SecretReveal } from './components/SecretReveal'
import {
  CATEGORY_KEY,
  CATEGORY_ORDER,
  vaultProviderName,
  type AccessCategory,
  type CompanyAccess,
} from './types'

export interface AccessVaultProps {
  companyId: string
  /** `can(me, 'access:reveal')`. En false no se renderiza ningún botón de revelar. */
  canReveal: boolean
  /** `can(me, 'access:write')`. */
  canWrite: boolean
}

function groupByCategory(accesses: readonly CompanyAccess[]) {
  const groups = new Map<AccessCategory, CompanyAccess[]>()
  for (const access of accesses) {
    const bucket = groups.get(access.category) ?? []
    bucket.push(access)
    groups.set(access.category, bucket)
  }

  return CATEGORY_ORDER.filter((category) => groups.has(category)).map((category) => ({
    category,
    items: (groups.get(category) ?? []).sort((a, b) => a.label.localeCompare(b.label)),
  }))
}

/**
 * El vault de accesos de la empresa, agrupado por categoría.
 *
 * Dos tipos de fila y la diferencia se ve de un vistazo:
 *
 * - **`vaulted`**: no hay secreto de este lado. Botón "Abrir en 1Password" con
 *   deep link `onepassword://`, que abre la app de escritorio en el ítem exacto.
 * - **`stored`**: el usuario en claro (es lo que uno copia diez veces por día) y
 *   el secreto detrás de un "mantener presionado" (`SecretReveal`).
 *
 * Los grupos van con lo crítico arriba porque es lo que se busca apurado, y
 * `prod` / `payments` son siempre `vaulted`: lo impone un CHECK de la base.
 */
export function AccessVault({ companyId, canReveal, canWrite }: AccessVaultProps) {
  const t = useT()
  const query = useCompanyAccesses(companyId)
  const [formOpen, setFormOpen] = useState(false)

  if (query.isPending) return <SkeletonRows rows={5} columns={[0.35, 0.25, 0.3]} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const accesses = query.data ?? []
  const groups = groupByCategory(accesses)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-[72ch] text-xs text-text-2">{t('accesses.intro')}</p>
        {canWrite ? (
          <Button variant="primary" onClick={() => setFormOpen(true)}>
            <Plus size={13} aria-hidden="true" />
            {t('accesses.new')}
          </Button>
        ) : null}
      </div>

      {groups.length === 0 ? (
        <EmptyState
          title={t('accesses.empty.title')}
          description={t('accesses.empty.description')}
          icon={<KeyRound size={18} aria-hidden="true" />}
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => setFormOpen(true)}>
                {t('accesses.empty.action')}
              </Button>
            ) : null
          }
        />
      ) : (
        groups.map((group) => (
          <section key={group.category} className="flex flex-col gap-1.5">
            <h3 className="flex items-center gap-2 text-2xs tracking-wide text-text-3 uppercase">
              {t(CATEGORY_KEY[group.category])}
              <span className="num text-text-3">{group.items.length}</span>
            </h3>

            <table
              className="hairline w-full table-fixed overflow-hidden rounded-[var(--radius-card)] bg-surface-1 text-xs"
              aria-label={t('accesses.groupTableLabel', {
                categoria: t(CATEGORY_KEY[group.category]),
              })}
            >
              <thead className="text-2xs tracking-wide text-text-3 uppercase">
                <tr className="hairline-b">
                  <th scope="col" className="w-[30%] px-2 py-1.5 text-left font-normal">
                    {t('accesses.field.access')}
                  </th>
                  <th scope="col" className="w-[24%] px-2 py-1.5 text-left font-normal">
                    {t('accesses.field.username')}
                  </th>
                  <th scope="col" className="px-2 py-1.5 text-left font-normal">
                    {t('accesses.field.secret')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((access) => (
                  <AccessRow key={access.id} access={access} canReveal={canReveal} />
                ))}
              </tbody>
            </table>
          </section>
        ))
      )}

      {canWrite ? (
        <AccessForm companyId={companyId} open={formOpen} onClose={() => setFormOpen(false)} />
      ) : null}
    </div>
  )
}

function AccessRow({ access, canReveal }: { access: CompanyAccess; canReveal: boolean }) {
  const t = useT()
  const deepLink = vaultDeepLink(access)

  return (
    <tr className="hairline-b align-top last:border-b-0">
      <td className="px-2 py-2">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5">
            {/*
              El punto era **la única excepción del sistema a "nunca solo
              color"**: 6px, `aria-hidden`, sin texto ni tooltip, distinguiendo
              `vaulted` de `stored` por el color y nada más. Quien no distingue
              esos dos colores no tenía cómo saber cuál era cuál (QA 7.4).

              Ahora lleva glifo, `title` y un texto para lector de pantalla. El
              glifo sigue la misma convención que el resto: ▲ es "vive afuera",
              ● es "lo guardamos nosotros".
            */}
            <span
              title={t(`accesses.kind.${access.kind}`)}
              className={cn(
                'num inline-flex shrink-0 items-center text-2xs leading-none',
                access.kind === 'vaulted' ? 'text-signal' : 'text-text-3',
              )}
            >
              <span aria-hidden="true">{access.kind === 'vaulted' ? '▲' : '●'}</span>
              <span className="sr-only">{t(`accesses.kind.${access.kind}`)}</span>
            </span>
            <span className="truncate text-text-1">{access.label}</span>
          </span>
          {access.url ? (
            <a
              href={access.url}
              target="_blank"
              rel="noreferrer"
              className="num inline-flex items-center gap-1 truncate text-2xs text-text-3 hover:text-signal"
            >
              {access.url}
              <ExternalLink size={10} aria-hidden="true" />
            </a>
          ) : null}
          {access.notes ? <span className="text-2xs text-text-3">{access.notes}</span> : null}
        </div>
      </td>

      <td className="px-2 py-2">
        {access.username ? (
          <span className="flex min-w-0 items-center gap-1">
            {/* El usuario va SIEMPRE en claro: no es el secreto y se copia diez veces por día. */}
            <span className="num truncate text-text-2">{access.username}</span>
            <CopyButton
              value={access.username}
              label={t('accesses.copyUsername', { acceso: access.label })}
            />
          </span>
        ) : (
          <span className="text-2xs text-text-3">{t('common.state.none')}</span>
        )}
      </td>

      <td className="px-2 py-2">
        {access.kind === 'vaulted' ? (
          <span className="flex flex-wrap items-center gap-1.5">
            {deepLink ? (
              <a
                href={deepLink}
                className={cn(
                  'hairline inline-flex h-6 items-center gap-1.5 rounded-[var(--radius-control)] px-2',
                  'text-2xs text-text-1 hover:bg-surface-2',
                )}
              >
                <ShieldCheck size={12} aria-hidden="true" />
                {t('accesses.vault.openIn', { gestor: vaultProviderName(access.vault_provider) })}
              </a>
            ) : (
              <span className="text-2xs text-text-3">
                {t('accesses.vault.referencedNoLink', {
                  gestor: access.vault_provider ?? t('accesses.vault.fallbackProvider'),
                })}
              </span>
            )}
            <span className="text-2xs text-text-3">{t('accesses.vault.notStoredHere')}</span>
          </span>
        ) : access.has_secret ? (
          <SecretReveal accessId={access.id} label={access.label} canReveal={canReveal} />
        ) : (
          <span className="text-2xs text-text-3">{t('accesses.noSecretStored')}</span>
        )}
      </td>
    </tr>
  )
}
